import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes';
import type { AudiencePricing } from '@openx/types/targeting/audience';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useDealContext } from '../../utils/context/DealContext';
import { FeeSummarySection } from './FeeSummarySection';
import { OrganizationPricingItem } from './OrganizationPricingItem';
import { PartnerFeeItem } from './PartnerFeeItem';
import { usePartnerFees } from './usePartnerFees';
import { createPricingConfig } from './utils/pricingCalculations';
import { createOrderedPartnerShares, needsPartnerShareSync } from './utils/segmentPartnerSync';

export type PricingDetailsEditProps = Partial<AudiencePricing> & {
  contextualPricing?: ContextualPricingApi | null;
  segmentPartnerRevenueMethod: SegmentPartnerRevenueMethod | null | undefined;
  isAdmin?: boolean;
};

export const PricingDetailsEdit = ({
  pricing_by_org,
  totals,
  contextualPricing,
  segmentPartnerRevenueMethod,
  isAdmin = false,
}: PricingDetailsEditProps) => {
  const { useCanEditSegmentPartnerPlatformShare } = useDealContext();
  const canEditPlatformFee = useCanEditSegmentPartnerPlatformShare();
  const {
    getValues,
    setValue: setFormValue,
    formState,
    control: formControl,
    trigger,
  } = useFormContext<DealFormDataWithTargeting>();

  const { rows, partnerIds, hasPlatformFee, shouldShowPartnerType, hasFees, feeSummary, organizations, isLoading } =
    usePartnerFees({
      pricing_by_org,
      totals,
      contextualPricing,
      segmentPartnerRevenueMethod,
    });

  useEffect(() => {
    // only users who can edit the platform fees sync them; for everyone else the form keeps the shares the API returned
    if (
      !canEditPlatformFee ||
      segmentPartnerRevenueMethod !== SegmentPartnerRevenueMethod.PoM ||
      partnerIds.length === 0
    )
      return;

    const current = getValues(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE);
    const ordered = createOrderedPartnerShares(partnerIds, current);

    if (needsPartnerShareSync(current, ordered)) {
      setFormValue(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE, ordered, { shouldDirty: false });
    }
  }, [canEditPlatformFee, segmentPartnerRevenueMethod, partnerIds, getValues, setFormValue]);

  // Non-admins only get their own organization's fee from the API, shown read-only.
  const observablePlatformShares = useWatch<
    DealFormDataWithTargeting,
    typeof dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE
  >({
    name: dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE,
  });

  const platformShareByPartnerId = useMemo(
    () => new Map(observablePlatformShares?.map(share => [share?.partner_id, share?.share])),
    [observablePlatformShares]
  );

  const triggerCrossFieldValidation = useCallback(() => {
    // Trigger gross_share field which displays the 94% error
    trigger('third_party_fees_config.0.gross_share');
  }, [trigger]);

  if (isLoading) {
    return <Loader />;
  }

  // the organizations only name the partners, so the fees show without them (by id) when their fetch fails
  if (!segmentPartnerRevenueMethod || !feeSummary || !hasFees) {
    return null;
  }

  const segmentErrors = formState.errors?.segment_partner_platform_share as
    | Array<{ share?: { message?: string } }>
    | Record<number, { share?: { message?: string } }>
    | undefined;

  return (
    <Box data-test="pricing-details">
      {rows.map(row => {
        const { partnerId } = row;
        const pricingConfig = createPricingConfig(segmentPartnerRevenueMethod, row.priceCpm, row.pricePom);
        const index = partnerIds.indexOf(partnerId);
        const fieldName = `segment_partner_platform_share.${index}` as const;
        const fieldErrorFromState = segmentErrors?.[index as keyof typeof segmentErrors]?.share?.message;

        return (
          <PartnerFeeItem
            key={`${row.partnerType}-${partnerId}`}
            row={row}
            organizationName={organizations[Number(partnerId)]?.name}
            shouldShowPartnerType={shouldShowPartnerType}
          >
            {!hasPlatformFee(row) ? (
              // no partnerId: the row shows the partner fee only, and has no platform fee field to bind
              <OrganizationPricingItem pricing={pricingConfig} isAdmin={false} isEditMode={true} />
            ) : canEditPlatformFee ? (
              <Controller
                name={fieldName}
                control={formControl}
                render={({ field, fieldState }) => (
                  <OrganizationPricingItem
                    pricing={pricingConfig}
                    isAdmin={isAdmin}
                    canEditPlatformFee
                    isEditMode={true}
                    partnerId={partnerId}
                    platformFeeValue={field.value?.share ?? ''}
                    onPlatformFeeChange={value => {
                      field.onChange({ partner_id: partnerId, share: value });
                      triggerCrossFieldValidation();
                    }}
                    platformFeeError={(fieldState.error?.message ?? fieldErrorFromState) as string | undefined}
                  />
                )}
              />
            ) : (
              <OrganizationPricingItem
                pricing={pricingConfig}
                isAdmin={isAdmin}
                isEditMode={true}
                partnerId={partnerId}
                platformFeeValue={String(platformShareByPartnerId.get(partnerId) ?? '')}
              />
            )}
          </PartnerFeeItem>
        );
      })}
      <FeeSummarySection summary={feeSummary} isLabelledByType={shouldShowPartnerType} />
    </Box>
  );
};
